import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Box, CircularProgress } from "@mui/material";
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  colorSchemeLight,
} from "ag-grid-community";

const vesselGridTheme = themeQuartz.withPart(colorSchemeLight).withParams({
  backgroundColor: "#ffffff",
  foregroundColor: "#1f2937",
  accentColor: "#3b82f6",
  headerTextColor: "#ffffff",
  headerBackgroundColor: "#1a325d",
  oddRowBackgroundColor: "#f9fafb",
  chromeBackgroundColor: "#f9fafb",
});

ModuleRegistry.registerModules([AllCommunityModule]);

const VesselVoyages = ({ vesselVoyages, loading = false }) => {
  const [addVesselModalOpen, setAddVesselModalOpen] = useState(false);
  const [selectedVoyage, setSelectedVoyage] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [search, setSearch] = useState("");
  const [deletePopup, setDeletePopup] = useState({
    isOpen: false,
    voyage: null,
  });
  const gridRef = useRef();

  const handleDeleteClick = (voyage) => {
    setDeletePopup({ isOpen: true, voyage });
  };

  const handleDeleteConfirm = () => {
    if (deletePopup.voyage) {
      setDeletePopup({ isOpen: false, voyage: null });
    }
  };

  const handleEdit = (voyage) => {
    console.log("voyage edit", voyage);

    setSelectedVoyage(voyage);
    setModalMode("edit");
    setAddVesselModalOpen(true);
  };

  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
    gridRef.current?.api?.setQuickFilter(e.target.value);
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "From Port",
        field: "fromPort",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "To Port",
        field: "toPort",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Departure Time",
        field: "departTime",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {formatDate(params.value)}
          </span>
        ),
      },
      {
        headerName: "ETA",
        field: "eta",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {formatDate(params.value)}
          </span>
        ),
      },
      {
        headerName: "Time Travelled",
        field: "timeTravelled",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value || "0 hours"}
          </span>
        ),
      },
      {
        headerName: "Distance Sailed",
        field: "distanceTravelled",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value?.toLocaleString() || "0"} nm
          </span>
        ),
      },
      {
        headerName: "Total Distance",
        field: "totalDistance",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value?.toLocaleString() || "0"} nm
          </span>
        ),
      },
      {
        headerName: "Avg Speed",
        field: "avgSpeed",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value?.toFixed(2) || "0"} knots
          </span>
        ),
      },
      {
        headerName: "Current Position",
        field: "position",
        cellRenderer: (params) => {
          const { currentLatitude, currentLongitude } = params.data || {};
          return (
            <span className="text-gray-700 text-sm font-medium">
              {currentLatitude && currentLongitude
                ? `${currentLatitude.toFixed(4)}°, ${currentLongitude.toFixed(
                    4
                  )}°`
                : "—"}
            </span>
          );
        },
      },
      {
        headerName: "HFO VLSFO",
        field: "hfoVLSFO",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value?.toLocaleString() || "0"} tons
          </span>
        ),
      },
      {
        headerName: "HFO HSFO",
        field: "hfoHSFO",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value?.toLocaleString() || "0"} tons
          </span>
        ),
      },
      {
        headerName: "LFO ULSFO",
        field: "lfoULSFO",
        cellRenderer: (params) => (
          <span className="text-gray-700 text-sm font-medium">
            {params.value?.toLocaleString() || "0"} tons
          </span>
        ),
      }
    ],
    []
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
        <span className="ml-3">Loading voyages...</span>
      </Box>
    );
  }

  return (
    <Box sx={{}}>
      {/* Table Container */}
      <Box
        className="ag-theme-quartz relative"
        sx={{
          width: "100%",
          overflow: "hidden",
        }}
      >
        <AgGridReact
          ref={gridRef}
          theme={vesselGridTheme}
          rowData={vesselVoyages}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50]}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
            flex: 1,
            minWidth: 150,
          }}
          domLayout="autoHeight"
          rowHeight={45}
          onGridReady={() => {
            setTimeout(() => {
              const header = document.querySelector(".ag-header");
              if (header) {
                header.style.background =
                  "linear-gradient(to top right, #1a325d, #27ade3)";
                header.style.color = "#ffffff";
              }
            }, 0);
          }}
          suppressHorizontalScroll={false}
        />
      </Box>
    </Box>
  );
};

export default VesselVoyages;
